import { useNavigate } from "react-router-dom";
import { MenuItem } from "./MenuData";
import { getProductConfigByInternalName, ProductInternalName, resolveInternalNameFromPathname } from "./productConfig";

/** Resolves pathname to product internal name (delegates to `resolveInternalNameFromPathname`). */
export const determineSelectedProduct = (pathname: string): string =>
  resolveInternalNameFromPathname(pathname);

const routeAliasMap: Record<string, string> = {
  "/kontrol/port-details": "/kontrol/port",
  "/kontrol/datacenter-details": "/kontrol/datacenter",
  "/kontrol/internet/internet-details": "/kontrol/internet",
  "/kontrol/backbone-details": "/kontrol/backbone",
  // Add other detail routes as needed
};

// **Filter Service Items Based on Selected Product**
export const getFilteredServiceItems = (
  product: string,
  items: MenuItem[]
): MenuItem[] => {
  if (product === ProductInternalName.KontrolEdge) {
    return items.filter(
      (item) =>
        item.path?.startsWith("/kontrol") ||
        item.children?.some((child) => child.path?.startsWith("/kontrol"))
    );
  }
  if (product === ProductInternalName.CtxReflector) {
    const reflectorItem = items.find((item) => item.label === "CTX Reflector");
    // Si lo encuentra y tiene hijos, devolvemos SOLO los hijos, así se omite el padre.
    if (reflectorItem && reflectorItem.children) {
      return reflectorItem.children;
    }
    return [];
  }
  return [];
};

/**
 * Dropdown product change → land on that product's Home/Overview.
 * Shares `getProductHomePath` with the post-login redirect (`ProductRedirect`)
 * so both flows always agree on each product's landing route.
 */
export const handleSelection = (
  selected: string,
  navigate: ReturnType<typeof useNavigate>
) => {
  if (selected === "Kontrol Edge") {
    navigate("/kontrol");
  } else if (selected === "CTX Reflector V2") {
    navigate("/reflector-v2/overview");
  } else if (selected === "CTX Reflector") {
    const cfg = getProductConfigByInternalName("CTX Reflector");
    navigate(cfg ? `${cfg.routePrefix}/status` : "/reflector/status");
  } else if (selected === "Insight") {
    navigate("/insight/home");
  }
};

export const expandMatchingParents = (
  items: MenuItem[],
  path: string,
  setExpandedSections: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >,
  setActiveItem: React.Dispatch<React.SetStateAction<string>>
): boolean => {
  for (const item of items) {
    if (item.children) {
      const foundInChildren = expandMatchingParents(
        item.children,
        path,
        setExpandedSections,
        setActiveItem
      );

      if (foundInChildren) {
        // Expand the parent section
        setExpandedSections((prev) => ({ ...prev, [item.label]: true }));
        return true;
      }
    }

    if (item.path && isPathMatch(item.path, path)) {
      // Use item.id if available, otherwise fall back to label for backward compatibility
      setActiveItem(item.id || item.label);
      return true;
    }
  }

  return false; // No match found at this level
};


export const findMenuItemByPath = (
  items: MenuItem[],
  path: string
): MenuItem | null => {
  for (const item of items) {
    if (item.path && isPathMatch(item.path, path)) return item;
    if (item.children) {
      const found = findMenuItemByPath(item.children, path);
      if (found) return found;
    }
  }
  return null;
};

const isPathMatch = (menuPath: string, currentPath: string): boolean => {
  const currentBase = currentPath.split("?")[0];  // Remove query params
  const menuBase = menuPath.split("?")[0];        // Normalize menuPath too

  // Direct match
  if (menuBase === currentBase) return true;

  /** Menu paths that are product "home" roots: must not match child URLs via prefix (e.g. /kontrol vs /kontrol/alerts). */
  const EXACT_ONLY_MENU_PATHS = new Set(["/kontrol"]);
  if (EXACT_ONLY_MENU_PATHS.has(menuBase)) {
    return false;
  }

  // Prefix match (for routes like /insight/topology that should match /insight/topology/*)
  if (currentBase.startsWith(menuBase + "/") || currentBase.startsWith(menuBase)) {
    // Make sure it's not a partial word match (e.g., /insight/home shouldn't match /insight/h)
    if (menuBase === currentBase || currentBase.startsWith(menuBase + "/")) {
      return true;
    }
  }

  // Handle aliases (e.g., port-details → port)
  const aliasTarget = routeAliasMap[currentBase];
  const matchResult = !!aliasTarget && aliasTarget === menuBase;
  
  return matchResult;
};





